/**
 * StudyOrganizer Context
 * Provides state management for the StudyOrganizer module
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import type {
  Protocol,
  StarredStudy,
  StudyFilters,
  StudyOrganizerState,
  EnrollmentStatus,
} from '../types';

interface StudyOrganizerContextType extends StudyOrganizerState {
  // Protocol actions (create/delete moved to eReg — P3, plan §3.1)
  fetchProtocols: () => Promise<void>;
  getProtocol: (id: string) => Promise<Protocol>;
  updateProtocol: (id: string, data: Partial<Protocol>) => Promise<Protocol>;

  // Starred studies actions
  toggleStar: (protocolId: string) => Promise<void>;
  isStarred: (protocolId: string) => boolean;

  // Filter actions
  setSearch: (search: string) => void;
  setSiteFilter: (siteId: string | null) => void;
  setStatusFilter: (statuses: EnrollmentStatus[]) => void;
  setShowStarredOnly: (show: boolean) => void;
  setShowArchived: (show: boolean) => void;
  clearFilters: () => void;

  // Computed
  filteredProtocols: Protocol[];
  myStudiesCount: number;
  allStudiesCount: number;
}

const StudyOrganizerContext = createContext<StudyOrganizerContextType | undefined>(undefined);

const initialFilters: StudyFilters = {
  search: '',
  siteId: null,
  statuses: [],
  showStarredOnly: false,
  showArchived: false,
};

export function StudyOrganizerProvider({ children }: { children: React.ReactNode }) {
  const { user, session } = useAuth();
  const [protocols, setProtocols] = useState<Protocol[]>([]);
  const [starredProtocolIds, setStarredProtocolIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<StudyFilters>(initialFilters);
  const [selectedProtocol, setSelectedProtocol] = useState<Protocol | null>(null);

  // Track if initial data has been fetched to avoid refetching on token refresh
  const initialFetchDone = useRef(false);
  // Use stable boolean instead of session object to avoid refetches on token refresh
  const isAuthenticated = !!session;

  // Fetch protocols
  const fetchProtocols = useCallback(async () => {
    if (!isAuthenticated) return;

    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (filters.siteId) params.set('site_id', filters.siteId);
      if (filters.statuses.length > 0) params.set('status', filters.statuses.join(','));
      if (filters.search) params.set('search', filters.search);

      const response = await api.get(`/api/siteorganizer/protocols?${params}`);
      setProtocols(response.data);
    } catch (err: any) {
      console.error('Error fetching protocols:', err);
      setError(err.response?.data?.error || 'Failed to fetch protocols');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, filters.siteId, filters.statuses, filters.search]);

  // Fetch starred studies
  const fetchStarredStudies = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      const response = await api.get('/api/siteorganizer/starred');
      const starredIds = new Set<string>(response.data.map((s: StarredStudy) => s.protocol_id));
      setStarredProtocolIds(starredIds);
    } catch (err: any) {
      console.error('Error fetching starred studies:', err);
    }
  }, [isAuthenticated]);

  // Initial fetch - only runs once when authenticated
  useEffect(() => {
    if (isAuthenticated && !initialFetchDone.current) {
      initialFetchDone.current = true;
      fetchProtocols();
      fetchStarredStudies();
    }
  }, [isAuthenticated, fetchProtocols, fetchStarredStudies]);

  // Refetch when filters change (but not on initial mount)
  useEffect(() => {
    if (isAuthenticated && initialFetchDone.current) {
      fetchProtocols();
    }
  }, [filters.siteId, filters.statuses, filters.search]);

  // Get single protocol
  const getProtocol = useCallback(async (id: string): Promise<Protocol> => {
    const response = await api.get(`/api/siteorganizer/protocols/${id}`);
    return response.data;
  }, []);

  // Update protocol (module workflow fields only — identity lives in eReg)
  const updateProtocol = useCallback(async (id: string, data: Partial<Protocol>): Promise<Protocol> => {
    const response = await api.patch(`/api/siteorganizer/protocols/${id}`, data);
    await fetchProtocols();
    return response.data;
  }, [fetchProtocols]);

  // Toggle star
  const toggleStar = useCallback(async (protocolId: string): Promise<void> => {
    const isCurrentlyStarred = starredProtocolIds.has(protocolId);

    try {
      if (isCurrentlyStarred) {
        await api.delete(`/api/siteorganizer/starred/${protocolId}`);
        setStarredProtocolIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(protocolId);
          return newSet;
        });
      } else {
        await api.post(`/api/siteorganizer/starred/${protocolId}`);
        setStarredProtocolIds(prev => new Set(prev).add(protocolId));
      }
    } catch (err: any) {
      console.error('Error toggling star:', err);
      throw err;
    }
  }, [starredProtocolIds]);

  // Check if starred
  const isStarred = useCallback((protocolId: string): boolean => {
    return starredProtocolIds.has(protocolId);
  }, [starredProtocolIds]);

  // Filter setters
  const setSearch = useCallback((search: string) => {
    setFilters(prev => ({ ...prev, search }));
  }, []);

  const setSiteFilter = useCallback((siteId: string | null) => {
    setFilters(prev => ({ ...prev, siteId }));
  }, []);

  const setStatusFilter = useCallback((statuses: EnrollmentStatus[]) => {
    setFilters(prev => ({ ...prev, statuses }));
  }, []);

  const setShowStarredOnly = useCallback((show: boolean) => {
    setFilters(prev => ({ ...prev, showStarredOnly: show }));
  }, []);

  const setShowArchived = useCallback((show: boolean) => {
    setFilters(prev => ({ ...prev, showArchived: show }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters(initialFilters);
  }, []);

  // Computed: active protocols (excludes inactive status unless showArchived is on)
  const activeProtocols = useMemo(() => {
    if (filters.showArchived) return protocols;
    return protocols.filter(p => p.status !== 'inactive');
  }, [protocols, filters.showArchived]);

  // Computed: filtered protocols
  const filteredProtocols = useMemo(() => {
    let result = [...activeProtocols];

    // Filter by starred if showing starred only
    if (filters.showStarredOnly) {
      result = result.filter(p => starredProtocolIds.has(p.id));
    }

    // Local search filter (in addition to server-side)
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      result = result.filter(p =>
        p.protocol_number.toLowerCase().includes(searchLower) ||
        p.sponsor_name?.toLowerCase().includes(searchLower) ||
        p.indication?.toLowerCase().includes(searchLower) ||
        p.study_nickname?.toLowerCase().includes(searchLower)
      );
    }

    return result;
  }, [activeProtocols, filters.showStarredOnly, filters.search, starredProtocolIds]);

  // Computed counts (based on active protocols only)
  const myStudiesCount = useMemo(() => {
    return activeProtocols.filter(p => starredProtocolIds.has(p.id)).length;
  }, [activeProtocols, starredProtocolIds]);

  const allStudiesCount = useMemo(() => {
    return activeProtocols.length;
  }, [activeProtocols]);

  const value: StudyOrganizerContextType = {
    protocols,
    starredProtocolIds,
    loading,
    error,
    filters,
    selectedProtocol,
    fetchProtocols,
    getProtocol,
    updateProtocol,
    toggleStar,
    isStarred,
    setSearch,
    setSiteFilter,
    setStatusFilter,
    setShowStarredOnly,
    setShowArchived,
    clearFilters,
    filteredProtocols,
    myStudiesCount,
    allStudiesCount,
  };

  return (
    <StudyOrganizerContext.Provider value={value}>
      {children}
    </StudyOrganizerContext.Provider>
  );
}

export function useStudyOrganizer() {
  const context = useContext(StudyOrganizerContext);
  if (context === undefined) {
    throw new Error('useStudyOrganizer must be used within a StudyOrganizerProvider');
  }
  return context;
}

export default StudyOrganizerContext;
